import type { UserProfile } from '../types';

const hasText = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number' || typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some((item) => hasText(item));
  }
  return false;
};

export const digitsOnly = (value: unknown): string => String(value || '').replace(/[^\d]/g, '');

export const formatSSN = (value: string) => {
  const digits = digitsOnly(value).slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

export const maskSSN = (value?: string) => {
  const digits = digitsOnly(value || '');
  return digits.length === 9 ? `***-**-${digits.slice(-4)}` : 'Not set';
};

export const isValidSSN = (value: string) => digitsOnly(value).length === 9;

export const normalizeProfileValue = (data: Partial<UserProfile>) => {
  const contactDetails = data.contactDetails || {};
  const address = data.permanentAddress || data.address || {};
  const bioData = data.bioData || {};

  const dateOfBirth = (data as any).birthDate || (data as any).dateOfBirth || bioData.dateOfBirth || '';
  const gender = data.gender || bioData.gender || '';
  const profilePhoto = data.profilePhoto || data.picture || '';

  return {
    firstName: data.firstName || '',
    middleName: (data as any).middleName || '',
    lastName: data.lastName || '',
    username: data.username || '',
    email: data.email || contactDetails.personalEmail || contactDetails.email || '',
    phone: data.phone || contactDetails.mobilePhone || (contactDetails as any).phone || '',
    ssn: data.ssn || '',
    dateOfBirth,
    gender,
    otherGender: (data as any).otherGender || '',
    profilePhoto,
    status: data.status || 'ACTIVE',
    maritalStatus: bioData.maritalStatus || '',
    occupation: bioData.occupation || '',
    address,
    permanentAddress: address,
    contactDetails,
  };
};

const valuesMatch = (left: unknown, right: unknown) => {
  const normalize = (value: unknown) =>
    typeof value === 'string' ? value.trim() : value === undefined || value === null ? '' : String(value);
  return normalize(left) === normalize(right);
};

export const buildProfileChangeSet = (data: Partial<UserProfile>, baseline: Partial<UserProfile> = {}) => {
  const normalized = normalizeProfileValue(data);
  const current = normalizeProfileValue(baseline);
  const currentByPropName = {
    ...current,
    birthDate: current.dateOfBirth,
  };

  const profileChanges = [
    ['firstName', normalized.firstName],
    ['middleName', normalized.middleName],
    ['lastName', normalized.lastName],
    ['username', normalized.username],
    ['birthDate', normalized.dateOfBirth],
    ['gender', normalized.gender],
    ['otherGender', normalized.otherGender],
    ['ssn', normalized.ssn],
    ['profilePhoto', normalized.profilePhoto],
    ['maritalStatus', normalized.maritalStatus],
  ]
    .filter(([name, value]) => {
      if (name === 'ssn') {
        const digits = digitsOnly(value);
        if (digits.length !== 9) return false;
        return digits !== digitsOnly((currentByPropName as any).ssn);
      }
      return hasText(value) && !valuesMatch(value, (currentByPropName as any)[name as string]);
    })
    .map(([name, value]) => ({
      name,
      value: name === 'ssn' ? digitsOnly(value) : value,
    }));

  const identityChanges = [
    ['firstName', normalized.firstName],
    ['middleName', normalized.middleName],
    ['lastName', normalized.lastName],
    ['username', normalized.username],
    ['email', normalized.email],
    ['phone', normalized.phone],
  ]
    .filter(([name, value]) => hasText(value) && !valuesMatch(value, (currentByPropName as any)[name as string]))
    .map(([name, value]) => ({ name, value }));

  return { profileChanges, identityChanges };
};

export const buildAddressPayload = (data: Partial<UserProfile>) => {
  const source = (data as any).permanentAddress || data.address || {};
  return {
    street: String(source.street || source.addressLine1 || '').trim(),
    city: source.city || '',
    state: source.state ? String(source.state).trim().toUpperCase() : '',
    zipCode: digitsOnly(source.zipCode || source.postalCode),
    country: source.country || '',
    county: source.county || '',
    latitude: source.latitude || '',
    longitude: source.longitude || '',
    placeId: source.placeId || '',
  };
};

export const hasAddressValues = (address: Record<string, any>) =>
  Object.values(address).some((value) => hasText(value));

export const validateAddressPayload = (address: Record<string, any>) => {
  if (!hasAddressValues(address)) return;

  const missing = [
    !String(address.street || '').trim() ? 'Street' : '',
    !String(address.city || '').trim() ? 'City' : '',
    !String(address.country || '').trim() ? 'Country' : '',
  ].filter(Boolean);

  if (missing.length) {
    throw new Error(`To save address, ${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required.`);
  }
};

export const buildRegistrationProfilePayload = (data: Partial<UserProfile> & { phone?: string; email?: string }) => {
  const normalized = normalizeProfileValue(data);
  return {
    id: data.id,
    firstName: normalized.firstName,
    middleName: normalized.middleName || undefined,
    lastName: normalized.lastName,
    username: normalized.username,
    status: normalized.status,
    profilePhoto: normalized.profilePhoto || undefined,
    contactDetails: {
      personalEmail: normalized.email || undefined,
      email: normalized.email || undefined,
      mobilePhone: normalized.phone || undefined,
    },
    permanentAddress: Object.values(normalized.permanentAddress || {}).some((value) => hasText(value))
      ? normalized.permanentAddress
      : undefined,
    bioData: Object.values((data.bioData || {}) as Record<string, unknown>).some((value) => hasText(value))
      ? {
          dateOfBirth: normalized.dateOfBirth || undefined,
          gender: normalized.gender || undefined,
          maritalStatus: normalized.maritalStatus || undefined,
        }
      : undefined,
  };
};
